import {
  Grid,
  IconButton,
  Menu,
  MenuItem,
  TextField,
  Tooltip,
  Box,
} from "@mui/material";
import { useState, type FC, MouseEvent, useMemo } from "react";
import MoreIcon from "@mui/icons-material/More";
import CancelRoundedIcon from "@mui/icons-material/CancelRounded";
import { MainMagicObjType, MainMagicPromptItemType } from "../MainPage";
import { MAGIC_TYPE_ENUM } from "../../utils/constants";

const PARTIAL_PROMPTS: MainMagicPromptItemType[] = [
  {
    name: "换衣服",
    prompt: "Replace the dress with a summer white T-shirt and denim shorts",
  },
  {
    name: "马甲线",
    prompt: "Add soft abs lines suitable for women",
  },
  {
    name: "锁骨清晰",
    prompt: "Make collarbones more defined and natural",
  },
  {
    name: "细腿",
    prompt: "Make leg lines straighter and slimmer",
  },
  {
    name: "细腰",
    prompt: "Make the waist appear slimmer and shapely",
  },
  {
    name: "丰胸",
    prompt: "Slightly enhance bust shape for a fuller appearance",
  },
  {
    name: "美甲",
    prompt: "Design pink-white gradient French nails for slender hands",
  },
  {
    name: "纹身",
    prompt: "Add a realistic rose tattoo to the shoulder",
  },
  {
    name: "换背景",
    prompt: "Change background to Eiffel Tower in Paris",
  },
  {
    name: "换天气",
    prompt: "Change to a sunny tropical beach scene",
  },
  {
    name: "婚纱照",
    prompt: "Transform to wedding dress in a chapel garden",
  },
  {
    name: "建筑设计-加人流、车流、绿植",
    prompt: "Add people, vehicles, and greenery to the rendering",
  },
  {
    name: "建筑结构修改",
    prompt: "Remove balcony and replace with floor- to-ceiling windows",
  },
  {
    name: "室内装饰-加沙发、柜子",
    prompt: "Add a beige sofa in modern minimalist style",
  },
];

const POWERFUL_PROMPTS: MainMagicPromptItemType[] = [
  {
    name: "雪天",
    prompt:
      "The weather turned into a blizzard, with heavy snowflakes falling. The snow was pristine white.",
  },
  {
    name: "去除人流",
    prompt: "Without crowds",
  },
];

const VIDEO_PROMPTS: MainMagicPromptItemType[] = [
  {
    name: "电影感短片",
    prompt: "生成一段具有电影感的短视频，氛围感强烈。",
  },
  {
    name: "卡通风格",
    prompt: "生成一段卡通风格的视频，画面活泼可爱。",
  },
];

type TextPromptProps = {
  magicType: MAGIC_TYPE_ENUM | null;
  magicObj: MainMagicObjType | null;
  textPrompt: string;
  onTextPromptChange: (textPrompt: string) => void;
  actionsDisabled: boolean;
  inShortcutFunc: boolean;
};

export const TextPrompt: FC<TextPromptProps> = ({
  magicType,
  magicObj,
  textPrompt,
  onTextPromptChange,
  actionsDisabled,
  inShortcutFunc,
}) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const openPromptsMenu = Boolean(anchorEl);

  const handleMoreIconBtnClick = (event: MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handlePromptsMenuClose = () => {
    setAnchorEl(null);
  };

  const promptsMenuOptions = useMemo(() => {
    if (magicObj) {
      let options: MainMagicPromptItemType[] = [];
      if (magicType === MAGIC_TYPE_ENUM.partial) {
        options = magicObj.partial.prompts;
      }

      if (magicType === MAGIC_TYPE_ENUM.powerful) {
        options = magicObj.powerful.prompts;
      }

      if (magicType === MAGIC_TYPE_ENUM.video) {
        options = magicObj.i2v.prompts;
      }

      return options;
    }

    return magicType === MAGIC_TYPE_ENUM.partial
      ? PARTIAL_PROMPTS
      : magicType === MAGIC_TYPE_ENUM.powerful
      ? POWERFUL_PROMPTS
      : magicType === MAGIC_TYPE_ENUM.video
      ? VIDEO_PROMPTS
      : [];
  }, [magicType, magicObj]);

  return (
    <Grid
      size={{ xs: 12 }}
      sx={{
        display: "flex",
        alignItems: "flex-start",
        width: "100%",
        gap: "8px",
        padding: "0 16px",
        zIndex: 1,
      }}
    >
      <TextField
        autoFocus
        autoComplete="off"
        fullWidth
        label="提示词"
        placeholder={
          magicType !== MAGIC_TYPE_ENUM.video
            ? "选择右侧的提示词建议，或者直接输入提示词，然后可以尝试文生图"
            : "选择右侧的提示词建议，或者直接输入提示词"
        }
        value={textPrompt}
        onChange={(e) => onTextPromptChange(e.target.value)}
        required
        size="small"
        multiline
        rows={5}
        disabled={actionsDisabled}
        color={inShortcutFunc ? "secondary" : "primary"}
      />

      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          justifyContent: "space-between",
          maxWidth: "45px",
          height: "100%",
        }}
      >
        <Tooltip title="提示词建议">
          <IconButton
            onClick={handleMoreIconBtnClick}
            size="small"
            color="success"
          >
            <MoreIcon />
          </IconButton>
        </Tooltip>

        <Tooltip title="清空提示词">
          <IconButton
            onClick={() => onTextPromptChange("")}
            size="small"
            color="error"
          >
            <CancelRoundedIcon />
          </IconButton>
        </Tooltip>

        <Menu
          anchorEl={anchorEl}
          open={openPromptsMenu}
          onClose={handlePromptsMenuClose}
        >
          {promptsMenuOptions.map((option, idx) => (
            <MenuItem
              key={`refer_tip_menu_item_${idx}`}
              onClick={() => {
                onTextPromptChange(option.prompt);
                handlePromptsMenuClose();
              }}
              selected={
                Boolean(textPrompt) &&
                option.prompt
                  .toLocaleLowerCase()
                  .includes(textPrompt.toLocaleLowerCase())
              }
              disabled={actionsDisabled}
            >
              {option.name}
            </MenuItem>
          ))}
        </Menu>
      </Box>
    </Grid>
  );
};
